import { api, ApiError, getToken, type RemoteCard, type RemoteImageMeta, type RemotePlace } from './api.ts';
import { db, getMeta, imageKey, setMeta, type LocalCard, type LocalImage, type LocalPlace } from './db.ts';
import { dirtyPrefs, markPrefsClean, takeRemotePrefs } from './prefs.ts';

/**
 * Delta sync against the server. Push what this device changed, pull what anything else changed,
 * resolve per card by `updatedAt` with the newer edit winning.
 *
 * There is deliberately no background sync: Safari implements none of the background-sync family,
 * so relying on it would give iOS users a wallet that silently stops backing up. Sync runs when
 * the app opens, when the network returns and when the tab becomes visible.
 */

export const toRemote = (c: LocalCard): RemoteCard => ({
  id: c.id,
  name: c.name,
  barcodeValue: c.barcodeValue,
  barcodeFormat: c.barcodeFormat,
  barcodeEcLevel: c.barcodeEcLevel ?? null,
  expiresOn: c.expiresOn ?? null,
  color: c.color,
  note: c.note,
  favorite: c.favorite,
  lastUsedAt: c.lastUsedAt,
  createdAt: c.createdAt,
  updatedAt: c.updatedAt,
  deleted: c.deleted,
});

export const toLocal = (c: RemoteCard): LocalCard => ({ ...c, dirty: 0 });

export const placeToRemote = ({ dirty: _dirty, ...p }: LocalPlace): RemotePlace => p;
export const placeToLocal = (p: RemotePlace): LocalPlace => ({ ...p, deleted: Boolean(p.deleted), dirty: 0 });

/**
 * The whole conflict policy, isolated so it can be tested without a database or a server.
 * A card the server has never seen, or that changed there more recently, wins; anything else
 * leaves the local copy — including its pending changes — alone.
 */
export function shouldTakeRemote(local: { updatedAt: number } | undefined, remote: { updatedAt: number }): boolean {
  if (!local) return true;
  return remote.updatedAt > local.updatedAt;
}

export interface SyncResult {
  status: 'ok' | 'offline' | 'unauthenticated' | 'skipped';
  pushed: number;
  pulled: number;
  imagesUp: number;
  imagesDown: number;
  at: number;
}

let running: Promise<SyncResult> | null = null;

/** Only one sync at a time; a second caller joins the one in flight rather than racing it. */
export function runSync(): Promise<SyncResult> {
  running ??= doSync().finally(() => {
    running = null;
  });
  return running;
}

async function doSync(): Promise<SyncResult> {
  const result: SyncResult = { status: 'ok', pushed: 0, pulled: 0, imagesUp: 0, imagesDown: 0, at: Date.now() };
  if (!getToken()) return { ...result, status: 'unauthenticated' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ...result, status: 'offline' };

  const since = await getMeta<number>('lastSync', 0);

  try {
    const dirty = await db.cards.where('dirty').equals(1).toArray();
    const dirtyPlaces = await db.places.where('dirty').equals(1).toArray();
    const prefs = await dirtyPrefs();
    if (dirty.length > 0 || dirtyPlaces.length > 0 || prefs) {
      const res = await api.push(dirty.map(toRemote), dirtyPlaces.map(placeToRemote), prefs ?? undefined);
      if (prefs) await markPrefsClean(prefs.updatedAt);
      const appliedPlaces = new Set(res.appliedPlaces ?? []);
      await db.transaction('rw', db.places, async () => {
        for (const place of dirtyPlaces) {
          if (!appliedPlaces.has(place.id)) continue;
          const current = await db.places.get(place.id);
          if (current && current.updatedAt === place.updatedAt) await db.places.put({ ...current, dirty: 0 });
        }
      });
      // Only the rows the server accepted are clean; a rejected row is stale and will be
      // replaced by the pull below.
      const applied = new Set(res.applied);
      await db.transaction('rw', db.cards, async () => {
        for (const card of dirty) {
          if (!applied.has(card.id)) continue;
          const current = await db.cards.get(card.id);
          // A card edited again while the request was in flight stays dirty.
          if (current && current.updatedAt === card.updatedAt) await db.cards.put({ ...current, dirty: 0 });
        }
      });
      result.pushed = applied.size;
    }

    result.imagesUp = await pushImages();

    const pulled = await api.pull(since);
    await db.transaction('rw', db.cards, async () => {
      for (const remote of pulled.cards) {
        const local = await db.cards.get(remote.id);
        if (shouldTakeRemote(local, remote)) {
          await db.cards.put(toLocal(remote));
          result.pulled++;
        }
      }
    });

    await db.transaction('rw', db.places, async () => {
      for (const remote of pulled.places ?? []) {
        const local = await db.places.get(remote.id);
        if (shouldTakeRemote(local, remote)) await db.places.put(placeToLocal(remote));
      }
    });
    if (pulled.prefs) await takeRemotePrefs(pulled.prefs);

    result.imagesDown = await pullImages(pulled.images);
    await setMeta('lastSync', pulled.serverTime);
    return result;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return { ...result, status: 'unauthenticated' };
    }
    // fetch() rejects with a TypeError when the network is unreachable. Nothing is lost: the
    // dirty rows are still dirty and the cursor has not moved.
    if (err instanceof TypeError) return { ...result, status: 'offline' };
    throw err;
  }
}

async function pushImages(): Promise<number> {
  const dirty = await db.images.where('dirty').equals(1).toArray();
  let count = 0;
  for (const img of dirty) {
    if (img.deleted) {
      await api.deleteImage(img.cardId, img.side);
    } else if (img.bytes) {
      await api.uploadImage(img.cardId, img.side, new Blob([img.bytes], { type: img.mime || 'image/jpeg' }));
    }
    const current = await db.images.get(img.key);
    if (current && current.updatedAt === img.updatedAt) await db.images.put({ ...current, dirty: 0 });
    count++;
  }
  return count;
}

async function pullImages(metas: RemoteImageMeta[]): Promise<number> {
  let count = 0;
  for (const meta of metas) {
    const key = imageKey(meta.cardId, meta.side);
    const local = await db.images.get(key);
    if (local && local.dirty === 1) continue; // local change wins until it has been pushed
    if (local && local.updatedAt >= meta.updatedAt) continue;

    if (meta.deleted) {
      await db.images.put({ key, cardId: meta.cardId, side: meta.side, bytes: null, mime: '', updatedAt: meta.updatedAt, deleted: true, dirty: 0 });
      count++;
      continue;
    }
    const blob = await api.downloadImage(meta.cardId, meta.side);
    if (!blob) continue;
    const row: LocalImage = {
      key, cardId: meta.cardId, side: meta.side, bytes: await blob.arrayBuffer(),
      mime: meta.mime, updatedAt: meta.updatedAt, deleted: false, dirty: 0,
    };
    await db.images.put(row);
    count++;
  }
  return count;
}

type Listener = (result: SyncResult) => void;
const listeners = new Set<Listener>();

export function onSync(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function syncAndNotify(): Promise<void> {
  try {
    const result = await runSync();
    for (const l of listeners) l(result);
  } catch {
    // A sync that throws is a bug worth fixing, not a reason to break the card the user opened.
  }
}

let debounce: ReturnType<typeof setTimeout> | undefined;

/** Ask for a sync soon. Repeated calls inside the window collapse into one request. */
export function requestSync(delay = 1200): void {
  clearTimeout(debounce);
  debounce = setTimeout(() => void syncAndNotify(), delay);
}

/** Wires the three moments iOS actually gives us. Returns a teardown for tests. */
export function startSyncScheduler(): () => void {
  const onOnline = () => requestSync(200);
  const onVisible = () => {
    if (document.visibilityState === 'visible') requestSync(200);
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  void syncAndNotify();
  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    clearTimeout(debounce);
  };
}
